import { useState } from "react";
import { useForm } from "antd/es/form/Form";
import { Button, Card, ConfigProvider, Flex, Form, Input, Modal, Table, TableProps, Typography } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { showDestructiveConfirm } from "components/modals/destructiveConfirm";
import { getAllPaymentMethods } from "hooks/queries/payment-methods";
import { useCreatePaymentMethod, useDeletePaymentMethod, useUpdatePaymentMethod } from "hooks/mutations/payment-methods";
import { translate } from "shared/languages";
import { PaymentMethod } from "shared/types";
import { th } from "shared/components/TableText";

// Add/edit share one Modal+Form — `editing` toggles which mutation fires,
// same pattern as every other business-owned named-list setting in this app.
const PaymentMethodFormModal = ({
  open,
  editing,
  onClose,
}: {
  open: boolean;
  editing: PaymentMethod | null;
  onClose: () => void;
}) => {
  const [form] = useForm();
  const { mutateAsync: create, isPending: isCreating } = useCreatePaymentMethod();
  const { mutateAsync: update, isPending: isUpdating } = useUpdatePaymentMethod();

  const onSubmit = () => {
    form
      .validateFields()
      .then(async (values: { name: string }) => {
        if (editing) {
          await update({ id: editing.id, name: values.name });
        } else {
          await create(values.name);
        }
        onClose();
        form.resetFields();
      })
      .catch(() => {
        // AntD already paints the inline field errors — nothing else to do.
      });
  };

  return (
    <Modal
      title={translate(editing ? "paymentMethods.edit_title" : "paymentMethods.add_title")}
      open={open}
      destroyOnClose
      onCancel={() => {
        onClose();
        form.resetFields();
      }}
      footer={
        <ConfigProvider theme={modalButtonsTheme}>
          <Flex justify="flex-end" gap={8}>
            <Button onClick={onClose}>{translate("button.cancel")}</Button>
            <Button type="primary" loading={isCreating || isUpdating} onClick={onSubmit}>
              {translate("paymentMethods.row_save")}
            </Button>
          </Flex>
        </ConfigProvider>
      }
    >
      <Form form={form} layout="vertical" initialValues={{ name: editing?.name }}>
        <Form.Item
          name="name"
          label={translate("paymentMethods.name_label")}
          rules={[{ required: true, message: translate("form.error.required") }]}
        >
          <Input placeholder={translate("paymentMethods.name_placeholder")} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const PaymentMethodsCard = () => {
  const { data: paymentMethods = [], isLoading } = getAllPaymentMethods();
  const { mutate: deletePaymentMethod } = useDeletePaymentMethod();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PaymentMethod | null>(null);

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (method: PaymentMethod) => {
    setEditing(method);
    setModalOpen(true);
  };

  const columns: TableProps<PaymentMethod>["columns"] = [
    { title: th(translate("paymentMethods.name_label")), dataIndex: "name" },
    {
      title: th(""),
      key: "actions",
      width: 90,
      render: (_, method) => (
        <Flex gap={4} justify="flex-end">
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(method)} />
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              showDestructiveConfirm({
                title: translate("paymentMethods.delete_confirm_title"),
                message: translate("paymentMethods.delete_confirm_content"),
                confirmLabel: translate("button.delete"),
                onConfirm: () => deletePaymentMethod(method.id),
              })
            }
          />
        </Flex>
      ),
    },
  ];

  return (
    <Card
      title={
        <Typography.Title level={5} style={{ margin: 0 }}>
          {translate("paymentMethods.card_title")}
        </Typography.Title>
      }
      extra={
        <Button icon={<PlusOutlined />} onClick={openAdd}>
          {translate("paymentMethods.add_button")}
        </Button>
      }
    >
      <Table<PaymentMethod>
        size="small"
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={paymentMethods}
        pagination={false}
        locale={{ emptyText: translate("paymentMethods.empty") }}
      />
      <PaymentMethodFormModal open={modalOpen} editing={editing} onClose={() => setModalOpen(false)} />
    </Card>
  );
};
